import React, { useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProgress } from '../contexts/ProgressContext';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './Certificate.css';

const Certificate = () => {
  const { user } = useAuth();
  const { progress } = useProgress();
  const certificateRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const generatePDF = async () => {
    if (!certificateRef.current) return;

    setIsGenerating(true);

    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0a0a0f'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Certificat_F12IA_${user?.firstName || 'Formation'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const completedModules = Object.values(progress.modules).filter(m => m.completed).length;
  const averageScore = Math.round(
    Object.values(progress.modules)
      .filter(m => m.quizScore !== null)
      .reduce((acc, m, _, arr) => acc + m.quizScore / arr.length, 0)
  );

  if (!progress.certificateUnlocked) {
    return (
      <div className="certificate-locked">
        <div className="locked-icon">🔒</div>
        <h3>Certificat verrouillé</h3>
        <p>Complétez tous les modules avec un score minimum de 70% pour débloquer votre certificat.</p>
        <div className="progress-status">
          <div className="status-item">
            <span className="status-value">{completedModules}/6</span>
            <span className="status-label">Modules validés</span>
          </div>
          <div className="status-item">
            <span className="status-value">{progress.totalProgress}%</span>
            <span className="status-label">Progression</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="certificate-container">
      <div className="certificate-preview">
        <div className="certificate" ref={certificateRef}>
          <div className="certificate-border">
            <div className="certificate-inner">
              <div className="certificate-header">
                <div className="certificate-logo">F12IA™</div>
                <div className="certificate-title">CERTIFICAT DE RÉUSSITE</div>
              </div>

              <div className="certificate-body">
                <p className="certificate-text">Ce certificat atteste que</p>
                <div className="certificate-name">
                  {user?.firstName} {user?.lastName}
                </div>
                <p className="certificate-text">a complété avec succès la formation</p>
                <div className="certificate-course">
                  Formation IA Complète
                </div>
                <p className="certificate-subtext">
                  Maîtrise de ChatGPT, Claude Code, Automatisation et Création d'Applications
                </p>
              </div>

              <div className="certificate-stats">
                <div className="stat">
                  <span className="stat-value">6</span>
                  <span className="stat-label">Modules</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{averageScore}%</span>
                  <span className="stat-label">Score moyen</span>
                </div>
                <div className="stat">
                  <span className="stat-value">6</span>
                  <span className="stat-label">Quiz validés</span>
                </div>
              </div>

              <div className="certificate-footer">
                <div className="certificate-date">
                  Délivré le {formatDate(progress.certificateDate)}
                </div>
                <div className="certificate-signature">
                  <div className="signature-line"></div>
                  <p>Directeur F12IA™</p>
                </div>
                <div className="certificate-id">
                  ID: F12IA-{Date.now().toString(36).toUpperCase()}
                </div>
              </div>

              <div className="certificate-decorations">
                <div className="decoration top-left"></div>
                <div className="decoration top-right"></div>
                <div className="decoration bottom-left"></div>
                <div className="decoration bottom-right"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="certificate-actions">
        <button
          className={`download-btn ${isGenerating ? 'generating' : ''}`}
          onClick={generatePDF}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <>
              <span className="spinner"></span>
              Génération en cours...
            </>
          ) : (
            <>
              📥 Télécharger le certificat (PDF)
            </>
          )}
        </button>
        <p className="certificate-info">
          🎉 Félicitations ! Vous avez terminé la formation F12IA™
        </p>
      </div>
    </div>
  );
};

export default Certificate;
